import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  alpha,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Fade,
  LinearProgress,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Alert,
} from '@mui/material';
import {
  Flag as GoalIcon,
  Warning as ChallengeIcon,
  Inventory as ResourceIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import default_theme from 'assets/themes/themes';

const WizardForm = ({ onFormSubmit, onCancel, onEdit, isAuthenticated = true }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    goals: '',
    challenges: '',
    resources: '',
    description: '',
    relatedFields: []
  });

  const availableFields = [
    'Agriculture & Farming',
    'Renewable Energy',
    'Water Management',
    'Tourism Development',
    'Digital Infrastructure',
    'Education & Training',
    'Healthcare Services',
    'Transportation',
    'Waste Management',
    'Community Development',
    'Environmental Protection',
    'Economic Development',
    'Social Services',
    'Cultural Heritage',
    'Innovation & Technology'
  ];

  const steps = [
    {
      label: 'Goals to Achieve',
      description: 'What are you trying to accomplish?',
      icon: <GoalIcon />,
      field: 'goals'
    },
    {
      label: 'Challenges Faced',
      description: 'What obstacles are you encountering?',
      icon: <ChallengeIcon />,
      field: 'challenges'
    },
    {
      label: 'Available Resources',
      description: 'What resources do you have access to?',
      icon: <ResourceIcon />,
      field: 'resources'
    },
    {
      label: 'Project Description',
      description: 'Brief overview of your project or initiative',
      icon: <DescriptionIcon />,
      field: 'description'
    },
    {
      label: 'Related Fields',
      description: 'Select relevant areas for your project',
      icon: <CategoryIcon />,
      field: 'relatedFields'
    }
  ];

  const handleInputChange = (field, value) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleField = (field) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setFormData(prev => ({
      ...prev,
      relatedFields: prev.relatedFields.includes(field)
        ? prev.relatedFields.filter(f => f !== field)
        : [...prev.relatedFields, field]
    }));
  };

  const handleNext = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setIsSubmitted(true);
    if (onFormSubmit) {
      onFormSubmit(formData);
    }
  };

  const handleEdit = (stepIndex) => {
    setIsSubmitted(false);
    setActiveStep(stepIndex);
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setActiveStep(0);
    setFormData({
      goals: '',
      challenges: '',
      resources: '',
      description: '',
      relatedFields: []
    });
  };

  const isStepValid = (stepIndex) => {
    const step = steps[stepIndex];
    if (step.field === 'relatedFields') {
      return formData[step.field].length > 0;
    }
    return formData[step.field].trim().length > 0;
  };

  const renderStepContent = (step, stepIndex) => {
    if (step.field === 'relatedFields') {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            Select all that apply:
          </Typography>
                  <FormGroup sx={{ maxHeight: 300, overflow: 'auto' }}>
            {availableFields.map((field) => (
              <FormControlLabel
                key={field}
                control={
                  <Checkbox
                    checked={formData.relatedFields.includes(field)}
                    onChange={() => toggleField(field)}
                    disabled={!isAuthenticated}
                    sx={{
                      color: default_theme.palette.other.chatGreen,
                      '&.Mui-checked': { color: default_theme.palette.other.chatGreen }
                    }}
                  />
                }
                label={field}
              />
            ))}
          </FormGroup>
        </Box>
      );
    }

    return (
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        value={formData[step.field]}
        onChange={(e) => handleInputChange(step.field, e.target.value)}
        placeholder={!isAuthenticated ? "Please sign in to fill out this form..." : `Describe your ${step.label.toLowerCase()}...`}
        disabled={!isAuthenticated}
        variant="outlined"
        sx={{ 
          mt: 2,
          '& .MuiInputBase-input::placeholder': {
            color: !isAuthenticated ? default_theme.palette.other.textMuted : default_theme.palette.other.textDisabledCustom,
            opacity: 1,
          }
        }}
      />
    );
  };

  // Summary view after submission
  if (isSubmitted) {
    return (
      <Fade in timeout={500}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            overflow: 'auto'
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 60, color: default_theme.palette.other.chatGreen, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: default_theme.palette.other.chatGreen, mb: 1 }}>
              Form Completed Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review your responses below. You can edit any section if needed.
            </Typography>
          </Box>

          <Paper
            elevation={2}
            sx={{
              p: 3,
              bgcolor: default_theme.palette.background.main,
              borderRadius: 2,
              maxWidth: 900,
              mx: 'auto',
              width: '100%'
            }}
          >
            {steps.map((step, index) => (
              <Box key={step.label}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: default_theme.palette.other.chatGreen,
                        color: default_theme.palette.primary.main,
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {step.label}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => {
                      if (!isAuthenticated) {
                        navigate('/login');
                        return;
                      }
                      handleEdit(index);
                    }}
                    disabled={!isAuthenticated}
                    sx={{
                      color: default_theme.palette.other.chatGreen,
                      '&:hover': { bgcolor: default_theme.palette.other.paleGreen }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>

                {step.field === 'relatedFields' ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 7 }}>
                    {formData.relatedFields.map((field) => (
                      <Chip
                        key={field}
                        label={field}
                        sx={{
                          bgcolor: default_theme.palette.other.paleGreen,
                          color: default_theme.palette.other.chatGreen,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      ml: 7,
                      color: default_theme.palette.primary.gray,
                      whiteSpace: 'pre-wrap',
                      bgcolor: default_theme.palette.primary.main,
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${default_theme.palette.border.white}`,
                    }}
                  >
                    {formData[step.field]}
                  </Typography>
                )}

                {index < steps.length - 1 && <Divider sx={{ my: 3 }} />}
              </Box>
            ))}
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/login');
                  return;
                }
                if (onFormSubmit) {
                  onFormSubmit(formData);
                }
                alert('Generating recommendations based on your inputs...');
              }}
              disabled={!isAuthenticated}
              sx={{
                            bgcolor: default_theme.palette.other.chatGreen,
                            '&:hover': { bgcolor: default_theme.palette.other.chatGreenHover },
                px: 4,
                py: 1.5
              }}
            >
              Generate Recommendations
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={!isAuthenticated}
              sx={{
                color: default_theme.palette.primary.gray,
                borderColor: default_theme.palette.border.white,
                '&:hover': { borderColor: default_theme.palette.primary.gray, bgcolor: default_theme.palette.primary.other }
              }}
            >
              Start Over
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  }

  // Wizard form view
  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: default_theme.palette.other.chatGreen, mb: 1 }}>
            Smart Recommendations Wizard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Help us understand your project to provide tailored solutions from our catalog
          </Typography>
          {!isAuthenticated && (
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                Please <Button variant="text" size="small" onClick={() => navigate('/login')} sx={{ textTransform: 'none', p: 0, minWidth: 'auto', color: default_theme.palette.other.figmaBlue }}>sign in</Button> to use the wizard and submit forms.
              </Typography>
            </Alert>
          )}
          <LinearProgress
            variant="determinate"
            value={(activeStep / (steps.length - 1)) * 100}
            sx={{
              mt: 2,
              height: 6,
              borderRadius: 3,
              bgcolor: default_theme.palette.primary.other,
              '& .MuiLinearProgress-bar': {
                bgcolor: default_theme.palette.other.chatGreen,
                borderRadius: 3
              }
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index <= activeStep ? default_theme.palette.other.chatGreen : default_theme.palette.border.white,
                        color: index <= activeStep ? default_theme.palette.primary.main : default_theme.palette.primary.gray,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {step.icon}
                    </Box>
                  }
                >
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>
                  
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      bgcolor: default_theme.palette.background.main,
                      border: alpha(default_theme.palette.border.white, '1px solid'),
                      borderRadius: 2
                    }}
                  >
                    {renderStepContent(step, index)}
                  </Paper>

                  <Box sx={{ mb: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                        disabled={!isAuthenticated || !isStepValid(index)}
                        sx={{
                          bgcolor: default_theme.palette.other.chatGreen,
                          '&:hover': { bgcolor: default_theme.palette.other.chatGreenHover },
                          '&:disabled': { 
                            bgcolor: default_theme.palette.border.white,
                            color: default_theme.palette.other.textMuted
                          }
                        }}
                      >
                        {index === steps.length - 1 ? 'Submit' : 'Continue'}
                      </Button>
                      
                      {index > 0 && (
                        <Button 
                          onClick={handleBack} 
                          disabled={!isAuthenticated}
                          sx={{ 
                            color: !isAuthenticated ? default_theme.palette.other.textDisabledCustom : default_theme.palette.primary.gray 
                          }}
                        >
                          Back
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => {
                          if (!isAuthenticated) {
                            navigate('/login');
                            return;
                          }
                          if (onCancel) onCancel();
                        }}
                        disabled={!isAuthenticated}
                        sx={{ 
                          color: !isAuthenticated ? default_theme.palette.other.textDisabledCustom : default_theme.palette.primary.gray, 
                          ml: 'auto', 
                          bgcolor: !isAuthenticated ? 'transparent' : default_theme.palette.other.chatGreenHover
                        }}
                      >
                        Skip for Now
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>
    </Fade>
  );
};

export default WizardForm;